import {
  CONTRACTOR_BADGE_GRADING_DECISION,
  getContractorBadgeVerificationsQuerySchema,
  getContractorBadgeVerificationsResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { endOfDay, isAfter, startOfDay, subDays } from "date-fns";
import { useMemo } from "react";

import { CONTRACTOR_BADGE_VERIFICATIONS_URL } from "../constants";

export enum BadgeVerificationWindowDays {
  /** Used for the booking flow to check for a recent badge */
  RECENT_BADGE = 30,
  /** Used for the policy page / dialog to count missed verifications */
  MISSED_BADGES = 60,
}

interface GetWindowDatesParams {
  windowDays: BadgeVerificationWindowDays;
  startDate?: string;
}

function getWindowDates(params: GetWindowDatesParams) {
  const { windowDays, startDate } = params;
  const today = new Date();
  const windowStart = startOfDay(subDays(today, windowDays));
  const windowEnd = endOfDay(today);

  const effectiveStart =
    isDefined(startDate) && isAfter(new Date(startDate), windowStart)
      ? startOfDay(new Date(startDate))
      : windowStart;

  return {
    gte: effectiveStart.toISOString(),
    lte: windowEnd.toISOString(),
  };
}

interface GetQueryParamsOptions {
  workerId: string;
  windowDays: BadgeVerificationWindowDays;
  startDate?: string;
  includeFinalGrade?: boolean;
  includeShift?: boolean;
}

function getQueryParams(options: GetQueryParamsOptions) {
  const { workerId, windowDays, startDate, includeFinalGrade, includeShift } = options;

  const include = [
    ...(includeFinalGrade ? ["finalGrade"] : []),
    ...(includeShift ? ["shift"] : []),
  ] as const;

  return {
    filter: {
      workerId: {
        eq: workerId,
      },
      "shift.start": getWindowDates({ windowDays, startDate }),
    },
    ...(include.length > 0 ? { include } : {}),
  };
}
// TECHNICALLY this would invaldate queries for other workers,
// but in practice they don't have the permissions to make other worker queries to begin with.
export const INVALIDATE_ALL_BADGE_QUERIES_KEY = [
  CONTRACTOR_BADGE_VERIFICATIONS_URL,
  "worker",
] as const;
export function getWorkerBadgeVerificationsQueryKey(
  workerId: string,
  windowDays: BadgeVerificationWindowDays = BadgeVerificationWindowDays.MISSED_BADGES,
  options: { includeFinalGrade?: boolean; includeShift?: boolean; startDate?: string } = {}
) {
  return [
    CONTRACTOR_BADGE_VERIFICATIONS_URL,
    "worker",
    getQueryParams({ workerId, windowDays, ...options }),
  ] as const;
}

interface UseGetWorkerBadgeVerificationsOptions {
  enabled?: boolean;
  includeMissedBadges?: boolean;
}

export function useGetWorkerBadgeVerifications(
  workerId: string,
  windowDays: BadgeVerificationWindowDays = BadgeVerificationWindowDays.MISSED_BADGES,
  options: UseGetWorkerBadgeVerificationsOptions = {}
) {
  const { enabled, includeMissedBadges = false } = options;

  const threeStrikeStartDateConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_THREE_STRIKE_START_DATE, {
    defaultValue: { enabled: false },
  });

  const startDate = threeStrikeStartDateConfig.enabled
    ? threeStrikeStartDateConfig.startDate
    : undefined;

  const includeOptions = {
    includeFinalGrade: includeMissedBadges,
    includeShift: includeMissedBadges,
  };

  const queryParams = getQueryParams({
    workerId,
    windowDays,
    startDate,
    ...includeOptions,
  });

  const query = useQuery({
    queryKey: getWorkerBadgeVerificationsQueryKey(workerId, windowDays, {
      ...includeOptions,
      startDate,
    }),
    queryFn: async () => {
      const response = await get({
        url: CONTRACTOR_BADGE_VERIFICATIONS_URL,
        queryParams,
        requestSchema: getContractorBadgeVerificationsQuerySchema,
        responseSchema: getContractorBadgeVerificationsResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.BADGE_VERIFICATION_GET_FAILURE,
    },
    ...(isDefined(enabled) ? { enabled } : {}),
  });

  const missedVerifications = useMemo(() => {
    if (!includeMissedBadges || !isDefined(query.data)) {
      return [];
    }

    return filterMissedVerifications(query.data.data, query.data.included ?? []);
  }, [includeMissedBadges, query.data]);

  const hasRecentBadge = useMemo(() => {
    if (!isDefined(query.data)) {
      return false;
    }

    return hasRecentBadgeInVerifications(query.data.data);
  }, [query.data]);

  return {
    ...query,
    missedVerifications,
    hasRecentBadge,
  };
}

interface VerificationData {
  attributes: { uploadedAt?: string; skippedAt?: string };
  relationships: { finalGrade?: { data: { id: string } } };
}

interface IncludedResource {
  type: string;
  id: string;
  attributes: Record<string, unknown>;
}

export function hasRecentBadgeInVerifications(verifications: VerificationData[]): boolean {
  return verifications.some((verification) => isDefined(verification.attributes.uploadedAt));
}

export function filterMissedVerifications(
  verifications: VerificationData[],
  included: IncludedResource[]
): VerificationData[] {
  return verifications.filter((verification) => {
    if (isDefined(verification.attributes.skippedAt)) {
      return true;
    }

    const gradeId = verification.relationships.finalGrade?.data?.id;
    if (!isDefined(gradeId)) {
      return false;
    }

    const grade = included.find(
      (resource) =>
        resource.type === "contractor-badge-verification-grade" && resource.id === gradeId
    );
    return (
      grade?.type === "contractor-badge-verification-grade" &&
      grade.attributes.decision === CONTRACTOR_BADGE_GRADING_DECISION.INCORRECT_BADGE_SUBMISSION
    );
  });
}
